import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { ClassicEditor } from 'ckeditor5';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { getEmptyPage, Page } from '../../interfaces/Page/Page';
import { createPage, deletePage, getPage, updatePage } from '../../api/Page';
import { sharedStyles } from '../../utils/Style';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { Waiting } from '../../components/Waiting/Waiting';
import { RichTextEditor } from '../../components/RichTextEditor/RichTextEditor';
import { TranslationAdornment } from '../../components/Translation/TranslationAdornment';

const width = 900;

export const ModifyPagePage = () => {
  const navigate = useNavigate();

  const editorRef = useRef<CKEditor<ClassicEditor>>(null);

  const { slug } = useParams<string>();
  const isEditing = !!slug;

  const [page, setPage] = useState(getEmptyPage());
  const [loading, setLoading] = useState(false);
  const [clickedDelete, setClickedDelete] = useState(false);

  const refreshPage = () => {
    setLoading(true);
    getPage(slug!)
      .then((p) => setPage(p))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isEditing) {
      refreshPage();
    }
  }, [slug]);

  const handleSave = () => {
    const body = editorRef.current?.editor?.getData() ?? '';
    const resultingPage: Page = { ...page, body };

    if (isEditing) {
      setLoading(true);
      updatePage(resultingPage)
        .then((updatedPage) => {
          toast.success('Sayfa başarıyla güncellendi');
          if (page.slug === updatedPage.slug) {
            setPage(updatedPage);
          } else {
            navigate(`/pages/pages/${updatedPage.slug}`);
          }
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      createPage(resultingPage)
        .then((createdPage) => {
          toast.success('Sayfa başarıyla oluşturuldu');
          navigate(`/pages/pages/${createdPage.slug}`);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    }
  };

  const handleDelete = () => {
    if (!isEditing) {
      return;
    }

    setLoading(true);
    deletePage(page.id)
      .then(() => {
        toast.success('Sayfa başarıyla silindi');
        goBack();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const goBack = () => navigate('/pages/pages');

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      {clickedDelete && (
        <ConfirmDialog
          content='Gerçekten seçili sayfayı silmek istiyor musunuz'
          open={clickedDelete}
          onClose={() => setClickedDelete(false)}
          onConfirm={handleDelete}
          color='error'
        />
      )}
      <Waiting open={loading} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5'>
          {isEditing ? 'Sayfayı Düzenle' : 'Yeni Sayfa'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {isEditing && (
          <Button
            variant='contained'
            color='error'
            size='small'
            startIcon={<DeleteIcon />}
            onClick={() => setClickedDelete(true)}
            sx={{ ...sharedStyles.buttonText }}
          >
            Sil
          </Button>
        )}
      </Box>
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
          height: 'fit-content',
          width: 'fit-content',
          boxSizing: 'border-box',
          p: 3,
          gap: 1.5,
        }}
      >
        <TextField
          label='İsim'
          variant='outlined'
          size='small'
          value={page.name}
          onChange={(e) => setPage({ ...page, name: e.target.value })}
          sx={{ width }}
          InputProps={{
            endAdornment: (
              <TranslationAdornment
                multiline
                defaultValue={page.name}
                value={page.nameTranslation}
                onChange={(x) => setPage({ ...page, nameTranslation: x })}
              />
            ),
          }}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Typography variant='h6'>İçerik</Typography>

          <TranslationAdornment
            richText
            defaultValue={page.body}
            value={page.bodyTranslation}
            onChange={(x) => setPage({ ...page, bodyTranslation: x })}
          />
        </Box>

        {!loading && (
          <RichTextEditor
            editorRef={editorRef}
            placeholder='Sayfa içeriği'
            initialData={page.body}
            width={width}
          />
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={page.isActive}
              onChange={() => setPage({ ...page, isActive: !page.isActive })}
            />
          }
          label={<Typography sx={sharedStyles.subtitle2}>Aktif</Typography>}
        />
        <Button
          variant='contained'
          onClick={handleSave}
          sx={{ ...sharedStyles.buttonText }}
        >
          Kaydet
        </Button>
      </Paper>
    </Box>
  );
};
